<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import Icon from '@iconify/svelte';
  import { Modal, Button, Card, Spinner, Alert, Fileupload } from 'flowbite-svelte';
  import supabase from "$lib/services/db";

  export let bucketName: string = 'content';
  export let folderPath: string = '';
  export let open: boolean = false;
  export let title: string = 'Media Gallery';
  export let selectedFilePath: string = '';
  export let acceptedFileTypes: string = 'image/*';
  export let itemsPerPage: number = 20;

  interface FileItem {
    name: string;
    size: number;
    created_at: string;
    updated_at: string;
    id: string;
    publicUrl: string;
    isSelected?: boolean;
    metadata?: Record<string, any>;
  }

  const dispatch = createEventDispatcher<{
    select: { filePath: string, publicUrl: string };
    close: void;
    upload: { filePath: string, publicUrl: string };
    delete: { filePath: string };
  }>();

  let files: FileItem[] = [];
  let filteredFiles: FileItem[] = [];
  let isLoading = false;
  let selectedFile: FileItem | null = null;
  let uploadFiles: FileList | undefined = undefined;
  let uploading = false;
  let errorMessage = '';
  let successMessage = '';
  let userHasSelectedFile = false;
  let maxFileSize: number = 5 * 1024 * 1024;
  let allowedMimeTypes: string[] = [];
  let bucketLoaded = false;

  let currentPage = 1;
  let totalPages = 1;
  let searchTerm = '';

  let deleteConfirmModal = false;
  let fileToDelete: FileItem | null = null;

  $: if (open) {
    loadFiles();
    userHasSelectedFile = false;
    currentPage = 1;
    searchTerm = '';
  }

  function filterFiles() {
    if (!searchTerm.trim()) {
      filteredFiles = [...files];
    } else {
      const term = searchTerm.toLowerCase();
      filteredFiles = files.filter(file =>
        file.name.toLowerCase().includes(term)
      );
    }

    totalPages = Math.ceil(filteredFiles.length / itemsPerPage);
    currentPage = Math.min(currentPage, totalPages || 1);
  }

  // Get current page items
  $: paginatedFiles = filteredFiles.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  async function loadFiles(): Promise<void> {
    try {
      isLoading = true;
      errorMessage = '';

      if (!bucketLoaded) {
        const { data: bucketData, error: bucketError } = await supabase.storage.getBucket(bucketName);

        if (bucketError) {
          console.error('Error fetching bucket info:', bucketError);
        } else if (bucketData) {
          if (bucketData.file_size_limit) {
            maxFileSize = bucketData.file_size_limit;
          }

          if (bucketData.allowed_mime_types && bucketData.allowed_mime_types.length > 0) {
            allowedMimeTypes = bucketData.allowed_mime_types;
            acceptedFileTypes = allowedMimeTypes.join(',');
          }

          bucketLoaded = true;
        }
      }

      const { data: fileData, error } = await supabase.storage
        .from(bucketName)
        .list(folderPath, {
          limit: 10000
        });

      if (error) {
        console.error('Error fetching files from Supabase:', error);
        errorMessage = `Error loading files: ${error.message}`;
        return;
      }

      files = await Promise.all((fileData || []).map(async (file) => {
        const { data } = supabase.storage.from(bucketName).getPublicUrl(folderPath + file.name);

        const fileItem: FileItem = {
          name: file.name,
          size: file.metadata?.size || 0,
          created_at: file.created_at,
          updated_at: file.updated_at || '',
          id: file.id,
          publicUrl: data.publicUrl,
          isSelected: folderPath + file.name === selectedFilePath
        };

        return fileItem;
      }));

      files.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      filterFiles();

      if (selectedFilePath) {
        selectedFile = files.find(file => folderPath + file.name === selectedFilePath) || null;
      }
    } catch (err) {
      console.error('Error in loadFiles:', err);
      errorMessage = `Unexpected error: ${err instanceof Error ? err.message : String(err)}`;
    } finally {
      isLoading = false;
    }
  }

  function selectFile(file: FileItem): void {
    userHasSelectedFile = true;

    const isAlreadySelected = selectedFile && selectedFile.id === file.id;

    if (isAlreadySelected) {
      files = files.map(f => ({
        ...f,
        isSelected: false
      }));

      filteredFiles = filteredFiles.map(f => ({
        ...f,
        isSelected: false
      }));

      selectedFile = null;
    } else {
      files = files.map(f => ({
        ...f,
        isSelected: f.id === file.id
      }));

      filteredFiles = filteredFiles.map(f => ({
        ...f,
        isSelected: f.id === file.id
      }));

      selectedFile = file;
    }
  }

  function applySelectedFile(): void {
    if (selectedFile) {
      dispatch('select', {
        filePath: folderPath + selectedFile.name,
        publicUrl: selectedFile.publicUrl
      });
      open = false;
    }
  }

  async function uploadFile(): Promise<void> {
    try {
      uploading = true;
      errorMessage = '';
      successMessage = '';

      if (!uploadFiles || uploadFiles.length === 0) {
        throw new Error('You must select a file to upload.');
      }

      const file = uploadFiles[0];

      // Check file size
      if (file.size > maxFileSize) {
        throw new Error(`File size exceeds the maximum allowed size (${Math.round(maxFileSize / 1024 / 1024)}MB).`);
      }

      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${file.name}`;
      const filePath = folderPath + fileName;

      let { error: uploadError, data } = await supabase.storage
        .from(bucketName)
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from(bucketName)
        .getPublicUrl(filePath);

      successMessage = 'File uploaded successfully!';

      uploadFiles = undefined;

      await loadFiles();

      const newFile = files.find(f => f.name === fileName);
      if (newFile) {
        selectFile(newFile);
      }

      dispatch('upload', {
        filePath: filePath,
        publicUrl: urlData.publicUrl
      });

    } catch (error: any) {
      errorMessage = `Upload error: ${error.message || 'Unknown error'}`;
      console.error('Error uploading file:', error);
    } finally {
      uploading = false;
    }
  }

  function confirmDelete(file: FileItem): void {
    fileToDelete = file;
    deleteConfirmModal = true;
  }

  function cancelDelete(): void {
    fileToDelete = null;
    deleteConfirmModal = false;
  }

  async function deleteFile(): Promise<void> {
    if (!fileToDelete) {
      console.error('No file selected for deletion');
      return;
    }

    const fileName = fileToDelete.name;
    const filePath = folderPath + fileName;
    const fileId = fileToDelete.id;

    try {
      errorMessage = '';

      const { error } = await supabase.storage
        .from(bucketName)
        .remove([filePath]);

      if (error) {
        console.error('Error deleting file:', error);
        errorMessage = `Error deleting file: ${error.message}`;
        return;
      }

      files = files.filter(f => f.id !== fileId);

      if (selectedFile && selectedFile.id === fileId) {
        selectedFile = null;
      }

      dispatch('delete', { filePath });

      successMessage = `File "${fileName}" deleted successfully.`;

      deleteConfirmModal = false;
      fileToDelete = null;

    } catch (error: any) {
      console.error('Error deleting file:', error);
      errorMessage = `Error deleting file: ${error.message || 'Unknown error'}`;
      deleteConfirmModal = false;
      fileToDelete = null;
    }
  }

  function close(): void {
    open = false;
    dispatch('close');
  }
</script>

<Modal bind:open size="xl" title={title} autoclose={false} outsideclose>
  <div class="max-h-[60vh] overflow-y-auto">
    {#if errorMessage}
      <Alert color="red" class="mb-4">
        {errorMessage}
      </Alert>
    {/if}

    {#if successMessage}
      <Alert color="green" class="mb-4">
        {successMessage}
      </Alert>
    {/if}

    <div class="mb-4">
      <p class="mt-1 mb-4 text-xs text-gray-500 dark:text-gray-400">
        Max file size: {Math.round(maxFileSize / 1024 / 1024)}MB
        {#if allowedMimeTypes.length > 0}
          • Allowed types: {allowedMimeTypes.join(', ')}
        {/if}
      </p>
      <div class="flex flex-col sm:flex-row gap-2">
        <div class="flex-1">
          <Fileupload
            id="file-upload"
            bind:files={uploadFiles}
            accept={acceptedFileTypes}
            disabled={uploading}
            class="mb-0 cursor-pointer"
          />
        </div>
        <Button color="blue" disabled={!uploadFiles || uploading} on:click={uploadFile}>
          {#if uploading}
            <Spinner size="4" class="mr-2" />
            Uploading...
          {:else}
            Upload
          {/if}
        </Button>
      </div>
    </div>

    <!-- Search bar -->
    <div class="mb-4">
      <div class="relative">
        <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
          <svg class="w-4 h-4 text-gray-500 dark:text-gray-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"/>
          </svg>
        </div>
        <input
          type="search"
          bind:value={searchTerm}
          on:input={filterFiles}
          class="block w-full p-2 pl-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
          placeholder="Search files..."
        />
      </div>
    </div>

    <div class="border-t border-gray-200 dark:border-gray-700 pt-4">
      {#if isLoading}
        <div class="flex justify-center items-center p-8">
          <Spinner size="8" />
        </div>
      {:else if filteredFiles.length === 0}
        <div class="p-8 text-center">
          <p class="text-gray-500 dark:text-gray-400">
            {searchTerm ? 'No files matching your search.' : 'No files found. Upload your first file!'}
          </p>
        </div>
      {:else}
        <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 p-4">
          {#each paginatedFiles as file (file.id)}
            <Card
              padding="sm"
              class="relative group cursor-pointer {file.isSelected ? 'ring-2 ring-blue-500' : ''}"
              on:click={() => selectFile(file)}
            >
              <div class="relative aspect-square overflow-hidden rounded-t-lg">
                {#if ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(file.name.toLowerCase().split('.').pop() || '')}
                  <img
                    src={file.publicUrl}
                    alt={file.name}
                    class="w-full h-full object-cover transition-transform group-hover:scale-105"
                  />
                {:else}
                  <div class="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-700">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                {/if}
                {#if file.isSelected}
                  <div class="absolute inset-0 bg-blue-500 bg-opacity-20 flex items-center justify-center">
                    <div class="bg-blue-500 text-white rounded-full p-1">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                {/if}
              </div>
              <div class="p-2">
                <p class="text-xs truncate">{file.name}</p>
                <p class="text-xs text-gray-500">{Math.round(file.size / 1024)} KB</p>
              </div>
              <button
                class="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                on:click|stopPropagation={() => confirmDelete(file)}
                aria-label="Delete file"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </Card>
          {/each}
        </div>

        <!-- Pagination -->
        {#if totalPages > 1}
          <div class="flex justify-center items-center mt-4 space-x-2">
            <Button
              size="xs"
              color="alternative"
              disabled={currentPage === 1}
              on:click={() => currentPage = Math.max(1, currentPage - 1)}
            >
              Previous
            </Button>

            <div class="flex items-center space-x-1">
              {#if currentPage > 2}
                <Button size="xs" color={currentPage === 1 ? "blue" : "alternative"} on:click={() => currentPage = 1}>1</Button>
              {/if}

              {#if currentPage > 3}
                <span class="text-gray-500">...</span>
              {/if}

              {#if currentPage > 1}
                <Button size="xs" color="alternative" on:click={() => currentPage = currentPage - 1}>{currentPage - 1}</Button>
              {/if}

              <Button size="xs" color="blue">{currentPage}</Button>

              {#if currentPage < totalPages}
                <Button size="xs" color="alternative" on:click={() => currentPage = currentPage + 1}>{currentPage + 1}</Button>
              {/if}

              {#if currentPage < totalPages - 2}
                <span class="text-gray-500">...</span>
              {/if}

              {#if currentPage < totalPages - 1}
                <Button size="xs" color={currentPage === totalPages ? "blue" : "alternative"} on:click={() => currentPage = totalPages}>{totalPages}</Button>
              {/if}
            </div>

            <Button
              size="xs"
              color="alternative"
              disabled={currentPage === totalPages}
              on:click={() => currentPage = Math.min(totalPages, currentPage + 1)}
            >
              Next
            </Button>
          </div>
        {/if}
      {/if}
    </div>
  </div>

  <div class="flex justify-end gap-2 mt-4">
    <Button color="alternative" on:click={close}>
      Cancel
    </Button>
    <Button color="blue" disabled={!selectedFile} on:click={applySelectedFile}>
      {selectedFile ? 'Apply Selected File' : 'Select a File'}
    </Button>
  </div>
</Modal>

<!-- Delete confirmation modal -->
<Modal bind:open={deleteConfirmModal} size="xs" autoclose={false} backdropClass="fixed inset-0 z-[51] bg-gray-900 bg-opacity-50 dark:bg-opacity-80" dialogClass="z-[52] fixed top-0 start-0 end-0 h-modal md:inset-0 md:h-full w-full p-4 flex">
  <div class="text-center">
    <Icon icon="heroicons:trash" color="#c81e1e" class="text-6xl mx-auto mb-4"/>

    <h3 class="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400">
      Are you sure you want to delete this file?
      {#if fileToDelete}
        <div class="mt-2 text-sm font-medium text-gray-900 dark:text-white">{fileToDelete?.name}</div>
      {/if}
    </h3>
    <div class="flex justify-center gap-2">
      <Button color="red" class="me-2" on:click={deleteFile}>Yes, delete it</Button>
      <Button color="alternative" on:click={cancelDelete}>No, cancel</Button>
    </div>
  </div>
</Modal>
